<template>
  <div
    class="working-hours"
    v-if="metaData"
  >
    <div v-if="!readonly || showWorkingHours">
      <label
        v-if="!hideLabel"
        :class="{ 'required-flag': isMandatory && !readonly }"
      >
        {{ $t(`tender.general.${metaData.fieldName}`) }}:</label>
      <ul
        :class="{ [metaData.addendumClassName]: metaData.isRevised }"
        class="working-hours-list"
        v-if="metaData.value"
      >
        <li
          class="row"
          v-for="(workingHour, index) in metaData.value"
          v-if="workingHour"
        >
          <div class="small-4 medium-4 large-4">
            {{ workingHour.workingDayFrom }} {{ (workingHour.workingDayTo && workingHour.workingDayTo !== '--/--' && workingHour.workingDayTo !== workingHour.workingDayFrom) ? `- ${workingHour.workingDayTo}` : '' }}
          </div>
          <div class="small-7 medium-7 large-7">
            {{ workingHour.workingHourFrom }} {{ (workingHour.workingHourTo && workingHour.workingHourTo !== '--/--' && workingHour.workingHourTo !== workingHour.workingHourFrom) ? `- ${workingHour.workingHourTo}` : '' }}
          </div>
          <a
            v-if="!readonly"
            class="fa fa-times fa-lg"
            :class="`delete-working-hour-${index}`"
            :aria-hidden="true"
            @click="deleteWorkingHour(index)"
          />
        </li>
      </ul>
      <hr v-if="!readonly && showWorkingHours">
    </div>
    <div
      v-if="!readonly"
      class="working-hours-add"
    >
      <HKLSelect
        class="workingDayFrom"
        name="workingDayFrom"
        :class="{ 'error-highlight': invalidWorkingHour || (isMandatory && emptyWorkingHours) }"
        :value="workingDayFrom || undefined"
        :options="workingDayFromHklOptions"
        :showSearch="true"
        placeholder=""
        @change="workingDayFrom = $event || ''"
      />
      <div class="dash" />
      <HKLSelect
        class="workingDayTo"
        name="workingDayTo"
        :class="{ 'error-highlight': invalidWorkingHour || (isMandatory && emptyWorkingHours)}"
        :disabled="workingDayFrom === 'PH'"
        :value="workingDayTo || undefined"
        :options="workingDayToHklOptions"
        :showSearch="true"
        placeholder=""
        @change="workingDayTo = $event || ''"
      />
      <HKLSelect
        class="workingHourFrom"
        name="workingHourFrom"
        :class="{ 'error-highlight': invalidWorkingHour || (isMandatory && emptyWorkingHours) }"
        :value="workingHourFrom || undefined"
        :options="workingHourFromHklOptions"
        :showSearch="true"
        placeholder=""
        @change="workingHourFrom = $event || ''"
      />
      <div class="dash" />
      <HKLSelect
        class="workingHourTo"
        name="workingHourTo"
        :class="{ 'error-highlight': invalidWorkingHour || (isMandatory && emptyWorkingHours)}"
        :disabled="workingHourFrom === 'Anytime'"
        :value="workingHourTo || undefined"
        :options="workingHourToHklOptions"
        :showSearch="true"
        placeholder=""
        @change="workingHourTo = $event || ''"
      />
      <button
        :disabled="disableButton()"
        type="button"
        class="btn btn--darkblue btn-add add-working-hour"
        id="modules-tenders-workingHours-add"
        name="add-working-hour"
        @click="addWorkingHour"
      >{{ $t('action.add') }}</button>
    </div>
  </div>
</template>

<script>
import EventBus from '@/utils/eventBus'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import { mapMutations } from 'vuex'
import mutationTypes from '@/store/mutation-types'
import { utils as formUtils } from '@/plugin/InputChange'
import { workingHourOptions, workingDayOptions, workingHourAnytime, workingDayPublicHoliday } from '@/utils/consts'

export default {
  name: 'WorkingHours',
  components: {
    HKLSelect,
  },
  props: {
    metaData: {
      type: Object,
      default: () => {},
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
    emptyWorkingHours: {
      type: Boolean,
      default: false,
    },
    isMandatory: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      workingHourOptions,
      workingDayOptions,
      workingDayFrom: '',
      workingDayTo: '',
      workingHourFrom: '',
      workingHourTo: '',
      fieldPath: ['general'],
      invalidWorkingHour: false,
      formUtils,
    }
  },

  computed: {
    showWorkingHours () {
      return ((this.metaData || {}).value || []).length
    },
    workingDayFromHklOptions () {
      return this.mapStringOptions(this.workingDayOptions, this.customWorkingDayLabel)
    },
    workingDayToHklOptions () {
      return this.mapStringOptions(
        this.workingDayOptions.slice(0, this.workingDayOptions.length - 1),
        this.customWorkingDayLabel,
      )
    },
    workingHourFromHklOptions () {
      return this.mapStringOptions(this.workingHourOptions, this.customWorkingHourLabel)
    },
    workingHourToHklOptions () {
      return this.mapStringOptions(
        this.workingHourOptions.slice(1, this.workingHourOptions.length),
        this.customWorkingHourLabel,
      )
    },
  },

  methods: {
    ...mapMutations('tenders', {
      updateValueByFieldPath: mutationTypes.TENDER_UPDATE_PATH_FIELD,
    }),

    mapStringOptions (options, labelFn) {
      return (options || []).map((value) => ({
        name: labelFn(value),
        value,
      }))
    },
    customWorkingDayLabel (option) {
      return this.$options.filters.customWorkingDayLabel(option)
    },
    customWorkingHourLabel (option) {
      return this.$options.filters.customWorkingHourLabel(option)
    },

    resetWorkingHour () {
      this.workingDayFrom = ''
      this.workingDayTo = ''
      this.workingHourFrom = ''
      this.workingHourTo = ''
      this.invalidWorkingHour = false
    },

    validateWorkingHour () {
      const hourFromIndex = this.workingHourOptions.indexOf(this.workingHourFrom)
      const hourToIndex = this.workingHourOptions.indexOf(this.workingHourTo)
      return (hourFromIndex === 0 || hourToIndex - hourFromIndex > 0)
    },

    disableButton () {
      return !(
        (this.workingHourFrom && (this.workingHourFrom === workingHourAnytime || this.workingHourTo)) &&
        (this.workingDayFrom && (this.workingDayFrom === workingDayPublicHoliday || this.workingDayTo))
      )
    },

    addWorkingHour () {
      if (!this.validateWorkingHour()) {
        this.invalidWorkingHour = true
        return
      }
      if (this.workingDayFrom === workingDayPublicHoliday) {
        this.workingDayTo = ''
      }
      if (this.workingHourFrom === workingHourAnytime) {
        this.workingHourTo = ''
      }
      const workingHour = {
        workingDayFrom: this.workingDayFrom,
        workingDayTo: this.workingDayTo,
        workingHourFrom: this.workingHourFrom,
        workingHourTo: this.workingHourTo,
      }

      // TODO @Michelle <Michelle_Zhang@epam.com> validate the workingHourTo > workingHourFrom
      const path = [...this.fieldPath, this.metaData.fieldName]
      this.updateValueByFieldPath({
        path,
        newValue: [
          ...this.metaData.value || [],
          workingHour,
        ],
      })
      this.resetWorkingHour()
      if (this.isMandatory) {
        this.$emit('reset:emptyWorkingHours')
      }
      formUtils.setDirty()
    },

    deleteWorkingHour (index) {
      const workingHours = JSON.parse(JSON.stringify(this.metaData)).value || []
      workingHours.splice(index, 1)
      const path = [...this.fieldPath, this.metaData.fieldName]
      this.updateValueByFieldPath({
        path,
        newValue: workingHours,
      })
      formUtils.setDirty()
    },
  },

  mounted () {
    EventBus.$on('modules_tenders_working_hours_clear', () => {
      this.resetWorkingHour()
    })
  },

  destroyed () {
    EventBus.$off('modules_tenders_working_hours_clear')
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/tender/view';
@import '~styles/tender/edit';
@import '~styles/tender/addendum';
@import '~styles/components/working-hours';
</style>

<style lang="scss">
@import '~styles/components/working-hours-add';
</style>
